/**
 * Utility to filter out non-DOM props before passing them to HTML elements
 * This prevents React warnings about unrecognized DOM properties
 */

// Props that should not be passed to DOM elements
const NON_DOM_PROPS = new Set([
  // Report card specific props
  'data',
  'baseData',
  'player',
  'playerAssessment',
  'reportCardLevel',
  'organizationLogo',
  'organizationLogoHeight',
  'hidePlainTextScores',
  'context',
  
  // Common React/component props that shouldn't go to DOM
  'children',
  'component',
  'ref',
  'key',
  
  // Add any other custom props that shouldn't be DOM attributes
]);

/**
 * Filters out props that shouldn't be passed to DOM elements
 * @param props - The props object to filter
 * @returns A new props object with non-DOM props removed
 */
export function filterDOMProps<T extends Record<string, any>>(props: T): Partial<T> {
  const filteredProps: Partial<T> = {};
  
  for (const [key, value] of Object.entries(props)) {
    // Skip props that are explicitly non-DOM
    if (NON_DOM_PROPS.has(key)) {
      continue;
    }
    
    // Skip props with camelCase that might be React component props
    // This catches organizationLogoHeight and similar props
    if (key.includes('Logo') || key.includes('Assessment') || key.includes('Player')) {
      continue;
    }
    
    // Skip function props (event handlers are ok, but component functions aren't)
    if (typeof value === 'function' && !key.startsWith('on')) {
      continue;
    }
    
    // Include the prop
    filteredProps[key as keyof T] = value;
  }
  
  return filteredProps;
}

/**
 * Extracts non-DOM props from a props object
 * @param props - The props object to extract from
 * @returns An object containing only the non-DOM props
 */
export function extractNonDOMProps<T extends Record<string, any>>(props: T): Partial<T> {
  const nonDOMProps: Partial<T> = {};
  
  for (const [key, value] of Object.entries(props)) {
    if (NON_DOM_PROPS.has(key) || 
        key.includes('Logo') || 
        key.includes('Assessment') || 
        key.includes('Player') ||
        (typeof value === 'function' && !key.startsWith('on'))) {
      nonDOMProps[key as keyof T] = value;
    }
  }
  
  return nonDOMProps;
}